import type { SupportedLanguage } from './index'

/**
 * Define localized routes here.
 * Each key maps to a route name, with translations for each language.
 *
 * Example:
 *   about: { it: 'chi-siamo', en: 'about-us' }
 *   → /it/chi-siamo, /en/about-us
 */
export const localizedRoutes = {
  home: {
    it: '',
    en: '',
    es: '',
    fr: '',
    de: '',
    pt: '',
  },
  about: {
    it: 'chi-siamo',
    en: 'about-us',
    es: 'sobre-nosotros',
    fr: 'a-propos',
    de: 'ueber-uns',
    pt: 'sobre-nos',
  },
  services: {
    it: 'servizi',
    en: 'services',
    es: 'servicios',
    fr: 'services',
    de: 'leistungen',
    pt: 'servicos',
  },
  machines: {
    it: 'macchine',
    en: 'machines',
    es: 'maquinas',
    fr: 'machines',
    de: 'maschinen',
    pt: 'maquinas',
  },
  news: {
    it: 'news',
    en: 'news',
    es: 'noticias',
    fr: 'actualites',
    de: 'news',
    pt: 'noticias',
  },
  events: {
    it: 'eventi',
    en: 'events',
    es: 'eventos',
    fr: 'evenements',
    de: 'veranstaltungen',
    pt: 'eventos',
  },
  partners: {
    it: 'partner',
    en: 'partners',
    es: 'partners',
    fr: 'partenaires',
    de: 'partner',
    pt: 'parceiros',
  },
  contacts: {
    it: 'contatti',
    en: 'contacts',
    es: 'contacto',
    fr: 'contact',
    de: 'kontakt',
    pt: 'contato',
  },
  privacy: {
    it: 'privacy',
    en: 'privacy',
    es: 'privacidad',
    fr: 'confidentialite',
    de: 'datenschutz',
    pt: 'privacidade',
  },
  cookiePolicy: {
    it: 'cookie-policy',
    en: 'cookie-policy',
    es: 'politica-cookies',
    fr: 'politique-cookies',
    de: 'cookie-richtlinie',
    pt: 'politica-cookies',
  },
  legalNotice: {
    it: 'note-legali',
    en: 'legal-notice',
    es: 'aviso-legal',
    fr: 'mentions-legales',
    de: 'impressum',
    pt: 'aviso-legal',
  },
} as const

export type RouteKey = keyof typeof localizedRoutes

/**
 * Get the localized path for a route
 */
export function getLocalizedPath(
  routeKey: RouteKey,
  lang: SupportedLanguage
): string {
  const path = localizedRoutes[routeKey][lang]
  return path ? `/${lang}/${path}` : `/${lang}`
}

/**
 * Find route key from a path segment
 */
export function findRouteKeyFromPath(
  pathSegment: string,
  lang: SupportedLanguage
): RouteKey | null {
  for (const [key, routes] of Object.entries(localizedRoutes)) {
    if (routes[lang] === pathSegment) {
      return key as RouteKey
    }
  }
  return null
}
